import { Button, Space, Table, Typography } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { StyledButton } from "../../../Common/Components";
import { PaginationComponent } from "../../../Common/Pagination/PaginationComponent";
import PermissionCheck from "../../../Common/PermissionCheck";
import countryCodes, { countryCodesDictionary } from "../../../config/countryCodes";
import timeZones from "../../../config/timeZones";
import { useFetchSeasonVenues } from "../../../entities/api";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useDeleteModal } from "../../../hooks/useDeleteModal";
import { useHandlePagination } from "../../../hooks/useHandlePagination";
import AddVenueToSeasonModal from "./AddVenueToSeasonModal";

const PermissionName = PermissionNames.VENUE;

const TableToolbar = styled.div`
  display: flex;
  justify-content: space-between;
`;

const getColumns = ({ formatMessage, customerId, sport, organizationId, handleDeleteIntent }) => [
  {
    title: formatMessage({
      id: "name.local",
      defaultMessage: "Local Name",
    }),
    dataIndex: "nameLocal",
  },
  {
    title: formatMessage({
      id: "country",
      defaultMessage: "Country",
    }),
    dataIndex: "countryCode",
    render: (value, row) => (value ? countryCodesDictionary[value] : ""),
    filters: countryCodes.map(([code, name]) => ({
      text: name,
      value: code,
    })),
    filterSearch: true,
    onFilter: (value, record) => record.countryCode === value,
  },
  {
    title: formatMessage({
      id: "timezone",
      defaultMessage: "Time Zone",
    }),
    dataIndex: "timezone",
    filters: timeZones.map((timezone) => ({
      text: timezone,
      value: timezone,
    })),
    filterSearch: true,
    onFilter: (value, record) => record.timezone === value,
  },
  {
    title:
      formatMessage({
        id: "venue",
        defaultMessage: "venue",
      }) + " id",
    dataIndex: "venueId",
  },
  {
    align: "right",
    render: (value, row) => (
      <>
        <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
          <StyledButton type="text" size="small">
            <Link to={`/${customerId}/admin/${sport}/${organizationId}/venues/manage/${row.venueId}`}>
              {formatMessage({
                id: "manage",
                defaultMessage: "Manage",
              })}
            </Link>
          </StyledButton>
        </PermissionCheck>
        {/*TODO check if needed*/}
        {/*<StyledButton type="text" size="small" onClick={() => handleDeleteIntent(row.venueId)}>*/}
        {/*  <i className="fas fa-trash" />*/}
        {/*</StyledButton>*/}
      </>
    ),
  },
];

const SeasonVenues = () => {
  const { formatMessage } = useIntl();
  const [filteredData, setFilteredData] = useState([]);
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(50);
  const { sport, organizationId, customerId } = useParams();

  const [isAddVenueToSeasonModalOpen, setIsAddVenueToSeasonModalOpen] = useState(false);
  const { data, isLoading } = useFetchSeasonVenues({
    limit: pageSize,
    offset,
  });

  const nextLink = useMemo(() => data?.links?.next, [data?.links?.next]);

  const pageRef = useRef();

  //TODO: disabled due to API unavailable - ticket CMAN-1054
  const disableAddVenueButton = true;

  useEffect(() => {
    if (data) {
      const filteredData = data?.data || [];
      setFilteredData(filteredData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const deleteModal = useDeleteModal({
    headingText: formatMessage({ id: "delete" }),
    refreshTrigger: () => {},
    pageRef,
    actionText: formatMessage({ id: "delete" }),
  });

  const handleDeleteIntent = (venueId) => {
    const URL = `/v1/${sport}/o/${organizationId}/venues/${venueId}`;
    deleteModal.setData.setUrl(URL);
    deleteModal.setData.setTextContent(formatMessage({ id: "delete.item.confirm" }));
    deleteModal.visibility.open();
  };

  return (
    <React.Fragment>
      {deleteModal.element()}
      <AddVenueToSeasonModal
        isOpen={isAddVenueToSeasonModalOpen}
        setIsOpen={setIsAddVenueToSeasonModalOpen}
        pageRef={pageRef}
        reloadData={() => {}}
      />
      <Space direction="vertical" style={{ width: "100%" }}>
        <TableToolbar>
          <Typography.Title level={4}>
            {formatMessage({
              id: "venues",
              defaultMessage: "Venues",
            })}
          </Typography.Title>
          <Space>
            {!disableAddVenueButton && (
              <Button
                type="primary"
                icon={<i className={"fas fa-plus"} style={{ marginRight: "5px" }} />}
                onClick={() => setIsAddVenueToSeasonModalOpen(true)}
              >
                <FormattedMessage id="venue.add" defaultMessage="Add Venue to Season" />
              </Button>
            )}
          </Space>
        </TableToolbar>
        <Table
          columns={getColumns({
            formatMessage,
            customerId: customerId,
            sport: sport,
            organizationId: organizationId,
            handleDeleteIntent,
          })}
          dataSource={filteredData}
          pagination={{
            current: currentPage,
            defaultPageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions,
            showQuickJumper: false,
            total: nextLink ? offset + filteredData.length + 1 : filteredData.length + offset,
            position: ["bottomCenter"],
            onChange: handlePaginationChange,
            showTotal: (total, range) => (
              <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range} />
            ),
          }}
          rowKey={(x) => x.venueId}
          size="small"
          loading={isLoading}
        />
      </Space>
    </React.Fragment>
  );
};

export default SeasonVenues;
